import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow, SectionHeading } from '@/components/generals/section-heading'
import { TreeRings } from '@/components/generals/tree-rings'
import { PageHeader } from '@/components/landing/page-header'
import { CtaBanner } from '@/components/landing/cta-banner'
import { buttonVariants } from '@/components/ui/button'
import { VALUES } from '@/constants/content'

const AboutPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="About us"
                description="Meet the small team of carpenters and designers behind Roamwood, and the principles behind every tiny house we build."
            />

            <PageHeader
                eyebrow="About us"
                title="A small workshop with a simple idea"
                description="Good homes do not need to be big. They need to be well designed, well built and made for the people who live in them."
            />

            <section id='story' className='overflow-hidden py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16'>
                    <div className='relative lg:col-span-5'>
                        <TreeRings className='pointer-events-none absolute -bottom-20 -left-24 size-[26rem] text-timber/20' />

                        <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-secondary sm:aspect-[5/4] lg:aspect-[4/5]'>
                            <Image
                                src='/images/craft.jpg'
                                alt="Hand tools with wooden handles laid out on a pale timber board"
                                fill
                                sizes='(min-width: 1024px) 40vw, 100vw'
                                className='object-cover'
                            />
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <Eyebrow>
                            Our story
                        </Eyebrow>

                        <h2 className='mt-4 font-heading text-4xl leading-tight font-medium tracking-tight text-balance sm:text-5xl'>
                            Why we build tiny houses
                        </h2>

                        <div className='mt-8 space-y-5 text-lg leading-relaxed text-muted-foreground'>
                            <p>
                                Roamwood is a team of carpenters, designers and makers who love working with wood. We started building tiny houses because we kept meeting people who wanted a simpler way to live: a home they could afford outright, that used less, and that could move with them.
                            </p>

                            <p>
                                Every house we make is designed with its future owners and built by our own hands, from the chassis to the last cupboard door. We do not outsource the parts that matter, and we never rush a build to hit a number.
                            </p>

                            <p>
                                The result is a small home that feels solid, warm and calm, and one you can be proud of for decades.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section id='values' className='border-y border-border/70 bg-secondary/50 py-16 lg:py-24'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    <SectionHeading
                        eyebrow="What we believe"
                        title="The principles behind every build"
                    />

                    <div className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                        {VALUES.map(value => (
                            <div key={value.title} className='rounded-2xl border border-border bg-card p-7'>
                                <span className='flex size-12 items-center justify-center rounded-xl bg-secondary text-primary'>
                                    <value.icon className='size-6' aria-hidden='true' />
                                </span>

                                <h3 className='mt-6 font-heading text-xl font-medium'>
                                    {value.title}
                                </h3>

                                <p className='mt-3 leading-relaxed text-muted-foreground'>
                                    {value.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id='workshop' className='py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20'>
                    <div>
                        <SectionHeading
                            eyebrow="The workshop"
                            title="Come and see where it happens"
                            description="The best way to understand how we build is to see it. Walk through homes in progress, feel the materials and meet the people who will build yours. Visits are by appointment so we can give you our full attention."
                        />

                        <Link href='/contact?visit=1' className={cn(buttonVariants({ size: 'lg' }), 'mt-9')}>
                            Book a workshop visit
                            <ArrowRight className='size-4' />
                        </Link>
                    </div>

                    <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-secondary'>
                        <Image
                            src='/images/workshop.jpg'
                            alt="Sunlit workbench with a hand plane, saw and measuring tools"
                            fill
                            sizes='(min-width: 1024px) 50vw, 100vw'
                            className='object-cover'
                        />
                    </div>
                </div>
            </section>

            <CtaBanner />
        </Layout>
    )
}

export default AboutPage
